import { TriangleAlert, Truck } from 'lucide-react';
import { memo } from 'react';

import { clsx } from 'clsx';
import { format } from 'date-fns';
import Image from 'next/image';
import Link from 'next/link';
import { toast } from 'sonner';

import { PersonalAccountAddressInfoType } from '@screens/PersonalAccount';
import { getProfileInfoClient } from '@screens/PersonalAccount/api/getProfileInfoClient/getProfileInfoClient';
import CdekLogo from '@shared/assets/icons/cdek-white.svg';
import { PHONE_NUMBER_LINK } from '@shared/constants/contacts';
import { ROUTES } from '@shared/constants/routes';
import { DeliveryCompany } from '@shared/types/enums/delivery.enum';
import { OrderProductType } from '@shared/types/order';
import { Button } from '@shared/ui/Button';
import { Divider } from '@shared/ui/Divider/Divider';
import { HStack, VStack } from '@shared/ui/Stack';
import { formatOrderString } from '@shared/utils/formatOrderString/formatOrderString';

import cls from './DeliveryPlate.module.scss';

export type DeliveryPlateProps = {
  deliveryCompany?: DeliveryCompany;
  deliveryStatus?: string;
  withAttention?: boolean;
  date?: string | null;
  amount?: number | null;
  orderNumber?: string;
  orderId?: string;
  trackNumber?: string | null;
  orderPrice?: number;
  address?: PersonalAccountAddressInfoType;
  products?: Pick<OrderProductType, 'productData' | 'quantity'>[];
};

export const DeliveryPlate = memo<DeliveryPlateProps>(
  ({
    date,
    amount,
    deliveryCompany,
    orderNumber,
    trackNumber,
    withAttention = true,
    address,
    products,
    orderPrice,
    deliveryStatus
  }) => {
    const isCdek = deliveryCompany === DeliveryCompany.CDEK;

    const isFree = !Number(amount) && isCdek;
    const showAttention = (amount && isCdek && withAttention) || !isCdek;

    const callManager = async () => {
      const { code, data } = await getProfileInfoClient();

      const comment = formatOrderString({ products: products, total: orderPrice, address: address });

      if (code === 200) {
        await fetch(
          `https://vigo.bitrix24.ru/rest/9224/vrniagyu9rxz5csu/crm.lead.add.json?FIELDS[TITLE]=Консультация по оформленному заказу Olevaro ${orderNumber}&FIELDS[NAME]=${data?.firstName}&FIELDS[PHONE][0][VALUE]=${data?.account.phone}&FIELDS[COMMENTS]=${comment}&FIELDS[LAST_NAME]=и${data?.lastName}&FIELDS[SECOND_NAME]=${data?.secondName}&FIELDS[SOURCE_ID]=WEB`
        ).then(() => {
          toast('Заявка на консультацию успешно отправлена! Ожидайте звонок от менеджера', {
            duration: 2000,
            position: 'bottom-center',
            style: {
              textAlign: 'center',
              color: '#ffffff',
              backgroundColor: '#18181b',
              border: 'none'
            }
          });
        });
      }
    };

    if ((!date && isCdek) || !deliveryCompany) {
      return null;
    }

    return (
      <VStack className={clsx(cls.wrapper, { [cls.free]: isFree })}>
        {isCdek && (
          <div>
            <HStack className={cls.titleWrapper}>
              <Truck />
              <h3 className={cls.title}>Доставка</h3>
            </HStack>
            <VStack className={cls.rowsWrapper}>
              {deliveryCompany === 'cdek' && deliveryStatus ? (
                <HStack className={cls.row}>
                  <div>Статус доставки</div>
                  <div>{deliveryStatus}</div>
                </HStack>
              ) : null}

              <HStack className={cls.row}>
                <div>Стоимость</div>
                <div className={clsx(cls.price, { [cls.free]: isFree })}>{isFree ? 'Бесплатно' : amount}</div>
              </HStack>
              <HStack className={cls.row}>
                <div>Примерная дата доставки</div>
                <div>{date ? format(new Date(date), 'dd.MM.yyyy') : ''}</div>
              </HStack>
              {isCdek && trackNumber && !withAttention && (
                <Link
                  href={`https://www.cdek.ru/ru/tracking/?order_id=${trackNumber}`}
                  target={'_blank'}
                >
                  <Button
                    className={cls.cdekButton}
                    size="l"
                    fullWidth
                    addonLeft={
                      <Image
                        src={CdekLogo}
                        width={50}
                        alt={''}
                      />
                    }
                  >
                    Ссылка на заказ
                  </Button>
                </Link>
              )}
              {showAttention && <Divider />}
            </VStack>
          </div>
        )}
        {showAttention && (
          <div className={cls.attention}>
            <HStack className={cls.titleWrapper}>
              <TriangleAlert color={'#fe9867'} />
              <h3 className={cls.title}>Доставка заказа</h3>
            </HStack>
            {isCdek ? (
              <p className={cls.text}>
                Доставка не включена в&nbsp;стоимость заказа. После оформления с Вами свяжется менеджер, подтвердит
                заказ и&nbsp;сообщит необходимые данные о&nbsp;доставке. Подробная информация о&nbsp;доставке
                на&nbsp;странице «
                <a
                  className={cls.link}
                  href={ROUTES.DELIVERY_AND_PAYMENT}
                >
                  Доставка и&nbsp;оплата
                </a>
                »
              </p>
            ) : (
              <p className={cls.text}>
                <Link
                  className={cls.link}
                  href={ROUTES.DELIVERY_AND_PAYMENT}
                >
                  Условия
                </Link>{' '}
                и сроки доставки озвучит менеджер после оформления заказа. Если есть вопросы - свяжитесь через чат на
                сайте или позвоните по номеру{' '}
                <Link
                  aria-label={`Позвонить на номер ${PHONE_NUMBER_LINK}`}
                  href={`tel:${PHONE_NUMBER_LINK}`}
                  className={cls.link}
                >
                  8&nbsp;(800)&nbsp;100&nbsp;40&nbsp;76
                </Link>
              </p>
            )}
            {!withAttention && (
              <Button
                className={cls.callManagerButton}
                fullWidth
                onClick={callManager}
              >
                Связаться с менеджером
              </Button>
            )}
          </div>
        )}
      </VStack>
    );
  }
);
